import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import api from '../../services/api';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

import OffboardingOverviewTab from './tab-pages/OffboardingOverviewTab';
import OffboardingTasksTab from './tab-pages/OffboardingTasksTab';
import OffboardingSubListTab from './tab-pages/OffboardingSubListTab';

import useOffboardingItAccessApi from '../offboarding-it-access/config';
import useOdHandoversApi from '../offboarding-handovers/config';
import useOdEquipmentApi from '../offboarding-equipment/config';
import useOdLegalDocsApi from '../offboarding-legal-docs/config';
import useOdExitInterviewsApi from '../offboarding-exit-interviews/config';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const HeaderLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 4px 0 12px;
  font-size: 18px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SubBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  background: #EEF1F4;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
`;

const BannerBase = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

// Doğrulama bekleyen satır varken sekmenin üstünde uyarı: "Erişim Kapatıldı"
// satırları bitmiş İŞ değildir; süreç bu satırların Doğrula'sını bekler.
const WarnBanner = styled(BannerBase)`
  background: ${({ theme }) => theme.colors.auxiliar.warning}22;
  border-left: 4px solid ${({ theme }) => theme.colors.auxiliar.warning};
`;

const ReadyBanner = styled(BannerBase)`
  background: ${({ theme }) => theme.colors.auxiliar.success}22;
  border-left: 4px solid ${({ theme }) => theme.colors.auxiliar.success};
`;

export default function OffboardingDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('OD') : false;
  const [offboarding, setOffboarding] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const loadOffboarding = useCallback(async () => {
    try {
      const res = await api.get(`/v1/offboardings/${id}`);
      setOffboarding(res.data?.data);
    } catch { /* interceptor toasts */ }
  }, [id]);

  useEffect(() => {
    let active = true;
    api.get(`/v1/offboardings/${id}`)
      .then((res) => { if (active) setOffboarding(res.data?.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [id]);

  // Per-row Doğrula on the BT Erişim tab. Mirrors the standalone page: the
  // button is offered to every writable user INCLUDING the revoker — the
  // four-eyes rule lives in the backend and returns an explanatory toast when
  // it applies (single-admin tenants may self-verify). Cancelled offboardings
  // keep their revoked rows as history, but Verify hard-409s on them
  // (OdParentGuard) — offer nothing there.
  const buildItAccessActions = useCallback((row, itApi, openConfirm) => {
    if (!row || !writable || row.status !== 'revoked' || offboarding?.status === 'cancelled') return [];
    return [{
      type: 'edit',
      label: t('offboardingItAccess.action.verify'),
      onClick: () => openConfirm({
        message: t('offboardingItAccess.confirm.verify', { system: row.systemName }),
        onYes: () => itApi.verify(row.id, ''),
      }),
    }];
  }, [writable, t, offboarding?.status]);

  const renderItAccessBanner = useCallback((rows, itApi) => {
    if (offboarding?.status === 'cancelled') return null;
    const relevant = (rows || []).filter((r) => r.status !== 'cancelled');
    const awaiting = relevant.filter((r) => r.status === 'revoked').length;
    if (awaiting > 0) {
      return (
        <WarnBanner>
          <span>{t('offboarding.detail.itAccess.awaitingVerifyBanner', { count: awaiting })}</span>
        </WarnBanner>
      );
    }
    // Pending/blocked rows also hold the gate shut (openItClosuresExist), but
    // closing them lives on the standalone page — say so instead of silence.
    const open = relevant.filter((r) => r.status === 'pending' || r.status === 'blocked').length;
    if (open > 0 && offboarding?.status === 'pending_it_zero_access') {
      return (
        <WarnBanner>
          <span>{t('offboarding.detail.itAccess.openClosuresBanner', { count: open })}</span>
        </WarnBanner>
      );
    }
    const allVerified = relevant.length > 0 && relevant.every((r) => r.status === 'verified');
    if (allVerified && offboarding?.status === 'pending_it_zero_access') {
      return (
        <ReadyBanner>
          <span>{t('offboarding.detail.itAccess.allVerifiedBanner')}</span>
          {writable && (
            <Button
              variant="secondary"
              onClick={() => setConfirmAction({
                message: t('offboarding.confirm.confirmItZeroAccess', { person: offboarding?.personFullName }),
                onYes: async () => {
                  try {
                    await api.post(`/v1/offboardings/${id}/confirm-it-zero-access`);
                    await loadOffboarding();
                    itApi.refresh();
                  } catch { /* interceptor toasts */ }
                },
              })}
            >
              {t('offboarding.action.confirmItZeroAccess')}
            </Button>
          )}
        </ReadyBanner>
      );
    }
    return null;
  }, [t, offboarding, writable, id, loadOffboarding]);

  const tabs = useMemo(() => [
    { key: 'overview', label: t('offboarding.detail.tab.overview') },
    { key: 'tasks', label: t('offboarding.detail.tab.tasks') },
    { key: 'itAccess', label: t('offboarding.detail.tab.itAccess') },
    { key: 'handover', label: t('offboarding.detail.tab.handover') },
    { key: 'equipment', label: t('offboarding.detail.tab.equipment') },
    { key: 'legalDocs', label: t('offboarding.detail.tab.legalDocs') },
    { key: 'exitInterview', label: t('offboarding.detail.tab.exitInterview') },
  ], [t]);

  const personName = offboarding?.personFullName || '...';

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('offboarding.pageTitle'), path: '/offboarding' },
          { label: personName },
        ]} />
        <HeaderLine>
          {personName}
          {offboarding?.exitType && (
            <SubBadge>{t(`offboarding.exitType.${offboarding.exitType}`, { defaultValue: offboarding.exitType })}</SubBadge>
          )}
          {offboarding?.status && (
            <SubBadge>{t(`offboarding.status.${offboarding.status}`, { defaultValue: offboarding.status })}</SubBadge>
          )}
        </HeaderLine>
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </StickyHeader>

      <TabContent>
        {activeTab === 'overview' && <OffboardingOverviewTab offboarding={offboarding} />}
        {activeTab === 'tasks' && <OffboardingTasksTab offboardingId={id} />}
        {activeTab === 'itAccess' && (
          <OffboardingSubListTab
            useApiHook={useOffboardingItAccessApi}
            offboardingId={id}
            title={t('offboarding.detail.tab.itAccess')}
            buildRowActions={writable ? buildItAccessActions : undefined}
            renderBanner={renderItAccessBanner}
          />
        )}
        {activeTab === 'handover' && (
          <OffboardingSubListTab useApiHook={useOdHandoversApi} offboardingId={id} title={t('offboarding.detail.tab.handover')} />
        )}
        {activeTab === 'equipment' && (
          <OffboardingSubListTab useApiHook={useOdEquipmentApi} offboardingId={id} title={t('offboarding.detail.tab.equipment')} />
        )}
        {activeTab === 'legalDocs' && (
          <OffboardingSubListTab useApiHook={useOdLegalDocsApi} offboardingId={id} title={t('offboarding.detail.tab.legalDocs')} />
        )}
        {activeTab === 'exitInterview' && (
          <OffboardingSubListTab useApiHook={useOdExitInterviewsApi} offboardingId={id} title={t('offboarding.detail.tab.exitInterview')} />
        )}
      </TabContent>

      <ConfirmDialog
        isOpen={!!confirmAction}
        message={confirmAction?.message}
        loading={confirmBusy}
        onConfirm={async () => {
          setConfirmBusy(true);
          try { await confirmAction.onYes(); } finally { setConfirmBusy(false); setConfirmAction(null); }
        }}
        onCancel={() => setConfirmAction(null)}
      />
    </PageContainer>
  );
}
